/**
 * Settings Page (General)
 *
 * Displays company information and general settings.
 */

import { useEffect } from 'react';
import { Logger, LogCategory } from '@/utils/logger';
import { AppLayout, PageHeader } from '@/components/layout';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useCompany } from '@/stores/authStore';
import { Building2, Globe, MapPin, Mail, Phone } from 'lucide-react';

export function SettingsPage() {
  const company = useCompany();

  useEffect(() => {
    Logger.info(LogCategory.UI, 'SettingsPage mounted', 'SettingsPage');
  }, []);

  if (!company) {
    return null;
  }

  return (
    <AppLayout>
      <PageHeader
        title="General Settings"
        description="Manage your company information and preferences"
      />

      <div className="max-w-3xl space-y-6">
        {/* Company Information */}
        <Card>
          <CardHeader
            title="Company Information"
            description="Basic information about your company"
          />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Company Name"
                value={company.name}
                disabled
                leftIcon={<Building2 className="h-4 w-4" />}
              />
              <Input
                label="Domain"
                value={company.domain}
                disabled
                leftIcon={<Globe className="h-4 w-4" />}
              />
              <Input
                label="Billing Email"
                value={company.billingEmail || ''}
                disabled
                leftIcon={<Mail className="h-4 w-4" />}
              />
              <Input
                label="Plan"
                value={company.plan.charAt(0).toUpperCase() + company.plan.slice(1)}
                disabled
              />
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        {company.address && (
          <Card>
            <CardHeader
              title="Business Address"
              description="Your registered business address"
            />
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Input
                    label="Street Address"
                    value={company.address.street}
                    disabled
                    leftIcon={<MapPin className="h-4 w-4" />}
                  />
                </div>
                <Input
                  label="City"
                  value={company.address.city}
                  disabled
                />
                <Input
                  label="State"
                  value={company.address.state}
                  disabled
                />
                <Input
                  label="ZIP Code"
                  value={company.address.zipCode}
                  disabled
                />
                <Input
                  label="Country"
                  value={company.address.country}
                  disabled
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Account Status */}
        <Card>
          <CardHeader
            title="Account Status"
            description="Your current account status and credits"
          />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-500">Plan</p>
                <p className="text-2xl font-bold text-slate-900 mt-1 capitalize">{company.plan}</p>
              </div>
              <div className="p-4 bg-primary-50 rounded-lg">
                <p className="text-sm font-medium text-primary-600">Credits Remaining</p>
                <p className="text-2xl font-bold text-primary-900 mt-1">{company.credits.toLocaleString()}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-500">Member Since</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {new Date(company.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Note */}
        <div className="text-sm text-slate-500 p-4 bg-slate-50 rounded-lg">
          <p>
            <strong>Note:</strong> Company information can only be updated by contacting support.
            This is a read-only view for MVP purposes.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
